import Matter from 'matter-js';
import { Application, Graphics } from 'pixi.js';

export interface MatterPixiDebugOptions {
  engine: Matter.Engine;
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  resolution?: number;
}

/** WebGL wireframe overlay for Matter bodies in test mode. */
export class MatterPixiDebugRenderer {
  private readonly engine: Matter.Engine;
  private readonly opts: MatterPixiDebugOptions;
  private app: Application | null = null;
  private graphics: Graphics | null = null;
  private readonly onAfterUpdate: () => void;

  constructor(opts: MatterPixiDebugOptions) {
    this.opts = opts;
    this.engine = opts.engine;
    this.onAfterUpdate = () => this.redraw();
  }

  async init(): Promise<void> {
    const { canvas, width, height, resolution = 1 } = this.opts;
    const app = new Application();
    await app.init({
      canvas,
      width,
      height,
      resolution,
      autoDensity: true,
      antialias: true,
      backgroundAlpha: 0.12,
      preference: 'webgl',
    });
    this.app = app;
    this.graphics = new Graphics();
    app.stage.addChild(this.graphics);
    Matter.Events.on(this.engine, 'afterUpdate', this.onAfterUpdate);
  }

  private redraw(): void {
    const g = this.graphics;
    if (!g) {
      return;
    }
    g.clear();
    const bodies = Matter.Composite.allBodies(this.engine.world);
    for (let b = 0; b < bodies.length; b++) {
      const body = bodies[b];
      if (body.render?.visible === false) {
        continue;
      }
      const verts = body.vertices;
      if (!verts?.length) {
        continue;
      }
      const v0 = verts[0];
      g.moveTo(v0.x, v0.y);
      for (let i = 1; i < verts.length; i++) {
        g.lineTo(verts[i].x, verts[i].y);
      }
      g.closePath();
      g.stroke({ width: 1, color: 0x00e676, alpha: 0.9 });
    }
  }

  destroy(): void {
    Matter.Events.off(this.engine, 'afterUpdate', this.onAfterUpdate);
    if (this.app) {
      this.app.destroy(true, { children: true, texture: true });
      this.app = null;
    }
    this.graphics = null;
  }
}
